import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { useMembershipService } from 'services/memberships';
import type { Membership } from 'types/membership';
import { MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS } from 'types/membership';
import Button from 'components/Button';
import {
  MEMBERSHIP_TYPE_FILTER_OPTIONS,
  membershipPassesFieldFilters,
  type MembershipGenderChoice,
} from 'utils/membershipListFilterUtils';
import styles from '../Membership.module.scss';

function formatName(m: Membership): string {
  const parts = [m.lastName, m.firstName].filter(Boolean);
  if (m.middleName) {
    parts.splice(1, 0, m.middleName);
  }
  return parts.join(', ') || '—';
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function PendingInvitations() {
  const membershipService = useMembershipService();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteIdFromUrl = searchParams.get('invite');
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const [invitations, setInvitations] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionKind, setActionKind] = useState<'accept' | 'decline' | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [genderFilter, setGenderFilter] = useState<MembershipGenderChoice[]>(
    [],
  );
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
  const [membershipTypeFilter, setMembershipTypeFilter] = useState<string[]>(
    [],
  );
  const [showFilters, setShowFilters] = useState(false);

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await membershipService.getPendingInvitations();
      setInvitations(data);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load pending membership invitations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [membershipService]);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  useEffect(() => {
    if (!inviteIdFromUrl || !highlightedRef.current || loading) return;
    highlightedRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [inviteIdFromUrl, loading, invitations]);

  const clearInviteParam = useCallback(() => {
    searchParams.delete('invite');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleAccept = useCallback(
    async (id: string) => {
      setActionError(null);
      setActionLoadingId(id);
      setActionKind('accept');
      try {
        await membershipService.acceptPendingInvitation(id);
        if (inviteIdFromUrl === id) clearInviteParam();
        await loadInvitations();
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'Could not accept invitation.',
        );
      } finally {
        setActionLoadingId(null);
        setActionKind(null);
      }
    },
    [membershipService, loadInvitations, inviteIdFromUrl, clearInviteParam],
  );

  const handleDecline = useCallback(
    async (id: string) => {
      setActionError(null);
      setActionLoadingId(id);
      setActionKind('decline');
      try {
        await membershipService.declinePendingInvitation(id);
        if (inviteIdFromUrl === id) clearInviteParam();
        await loadInvitations();
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'Could not decline invitation.',
        );
      } finally {
        setActionLoadingId(null);
        setActionKind(null);
      }
    },
    [membershipService, loadInvitations, inviteIdFromUrl, clearInviteParam],
  );

  const filteredInvitations = useMemo(
    () =>
      invitations.filter((m) =>
        membershipPassesFieldFilters(m, {
          gender: genderFilter,
          department: departmentFilter,
          membershipType: membershipTypeFilter,
        }),
      ),
    [invitations, genderFilter, departmentFilter, membershipTypeFilter],
  );

  const toggleValue = useCallback(
    (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
      setter((previous) =>
        previous.includes(value)
          ? previous.filter((v) => v !== value)
          : [...previous, value],
      );
    },
    [],
  );

  const toggleGenderValue = useCallback(
    (
      value: MembershipGenderChoice,
      setter: React.Dispatch<React.SetStateAction<MembershipGenderChoice[]>>,
    ) => {
      setter((previous) =>
        previous.includes(value)
          ? previous.filter((v) => v !== value)
          : [...previous, value],
      );
    },
    [],
  );

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Pending membership invitations</h1>
      </header>

      {!loading && !error && invitations.length > 0 && (
        <>
          <div className={styles.columnFilterControls}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <Filter size={14} />
              {showFilters ? 'Hide Filters' : 'Filter'}
            </Button>
          </div>
          {showFilters && (
            <div
              className={styles.membershipFiltersRow}
              aria-label="Filter invitations"
            >
              <div className={styles.filterGroup}>
                <div className={styles.filterGroupTitle}>Gender</div>
                <label className={styles.filterCheckLabel}>
                  <input
                    type="checkbox"
                    checked={genderFilter.includes('male')}
                    onChange={() => toggleGenderValue('male', setGenderFilter)}
                  />
                  Male
                </label>
                <label className={styles.filterCheckLabel}>
                  <input
                    type="checkbox"
                    checked={genderFilter.includes('female')}
                    onChange={() =>
                      toggleGenderValue('female', setGenderFilter)
                    }
                  />
                  Female
                </label>
              </div>
              <div className={styles.filterGroup}>
                <div className={styles.filterGroupTitle}>
                  Department / Ministry
                </div>
                {MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.filterCheckLabel}>
                    <input
                      type="checkbox"
                      checked={departmentFilter.includes(opt)}
                      onChange={() => toggleValue(opt, setDepartmentFilter)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
              <div className={styles.filterGroup}>
                <div className={styles.filterGroupTitle}>Membership type</div>
                {MEMBERSHIP_TYPE_FILTER_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.filterCheckLabel}>
                    <input
                      type="checkbox"
                      checked={membershipTypeFilter.includes(opt)}
                      onChange={() => toggleValue(opt, setMembershipTypeFilter)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setGenderFilter([]);
                  setDepartmentFilter([]);
                  setMembershipTypeFilter([]);
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </>
      )}

      {loading && (
        <div className={styles.loadingState}>
          <p>Loading pending invitations...</p>
        </div>
      )}

      {!loading && error && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button type="button" onClick={() => void loadInvitations()}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && actionError && (
        <div className={styles.errorState} role="alert">
          <p>{actionError}</p>
        </div>
      )}

      {!loading && !error && invitations.length === 0 && (
        <div className={styles.emptyState}>
          <p>You have no pending membership invitations.</p>
        </div>
      )}

      {!loading &&
        !error &&
        invitations.length > 0 &&
        filteredInvitations.length === 0 && (
          <div className={styles.emptyState}>
            <p>No invitations match the current filters.</p>
          </div>
        )}

      {!loading && !error && filteredInvitations.length > 0 && (
        <div className={styles.cardList}>
          {filteredInvitations.map((invitation) => {
            const isHighlighted = inviteIdFromUrl === invitation.id;
            return (
              <div
                key={invitation.id}
                id={`invite-${invitation.id}`}
                ref={isHighlighted ? highlightedRef : undefined}
                className={`${styles.card} ${isHighlighted ? styles.cardInviteHighlight : ''}`}
              >
                <div className={styles.cardMain}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardName}>
                      {invitation.company?.name || 'Unknown company'}
                    </div>
                  </div>
                  <div className={styles.cardEmail}>
                    {formatName(invitation)} &mdash; {invitation.email || '—'}
                  </div>
                </div>
                <div className={styles.cardActions}>
                  <div>
                    <strong>Invited on:</strong>{' '}
                    {formatDateTime(invitation.createdAt)}
                  </div>
                  {invitation.membershipDate && (
                    <div>
                      <strong>Membership date:</strong>{' '}
                      {formatDateTime(invitation.membershipDate)}
                    </div>
                  )}
                  {invitation.status && (
                    <div>
                      <strong>Status:</strong> {invitation.status}
                    </div>
                  )}
                </div>
                <div className={styles.invitationButtons}>
                  <Button
                    type="button"
                    size="sm"
                    disabled={actionLoadingId === invitation.id}
                    onClick={() => void handleAccept(invitation.id)}
                  >
                    {actionLoadingId === invitation.id &&
                    actionKind === 'accept'
                      ? 'Accepting…'
                      : 'Accept'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={actionLoadingId === invitation.id}
                    onClick={() => void handleDecline(invitation.id)}
                  >
                    {actionLoadingId === invitation.id &&
                    actionKind === 'decline'
                      ? 'Declining…'
                      : 'Decline'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PendingInvitations;
